import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import React from 'react';
import { DayButton, DayPicker, getDefaultClassNames } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { buttonVariants, cn } from '@/utils/utils';

function Calendar({
  buttonVariant = 'ghost',
  captionLayout = 'label',
  className,
  classNames,
  components,
  fixedWeeks = true,
  formatters,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
}) {
  const defaultClassNames = getDefaultClassNames();
  const { i18n } = useTranslation();

  return (
    <DayPicker
      captionLayout={captionLayout}
      className={cn(
        'bg-background group/calendar w-[270px] max-w-full px-2 pt-2 pb-1.5 [--cell-size:1.9rem] in-data-[slot=card-content]:bg-transparent in-data-[slot=popover-content]:bg-transparent',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      classNames={{
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[--cell-size] w-[--cell-size] shrink-0 select-none p-0 aria-disabled:opacity-50 pointer-events-auto relative z-10',
          defaultClassNames.button_next,
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'h-[--cell-size] w-[--cell-size] shrink-0 select-none p-0 aria-disabled:opacity-50 pointer-events-auto relative z-10',
          defaultClassNames.button_previous,
        ),
        caption_label: cn(
          'select-none font-medium',
          captionLayout === 'label'
            ? 'text-sm'
            : '[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5',
          defaultClassNames.caption_label,
        ),
        day: cn(
          'group/day relative aspect-square h-full w-full select-none p-0 text-center [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day,
        ),
        disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
        dropdown: cn('hidden', defaultClassNames.dropdown),
        dropdown_root: cn('relative', defaultClassNames.dropdown_root),
        dropdowns: cn(
          'flex h-[--cell-size] items-center justify-center gap-1 text-sm font-medium',
          defaultClassNames.dropdowns,
        ),
        hidden: cn('invisible', defaultClassNames.hidden),
        month: cn('flex min-h-[248px] w-full flex-col gap-3', defaultClassNames.month),
        month_caption: cn(
          'flex h-[--cell-size] w-full items-center justify-center px-[--cell-size]',
          defaultClassNames.month_caption,
        ),
        months: cn('relative flex flex-col gap-3 md:flex-row', defaultClassNames.months),
        nav: cn(
          'absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1 pointer-events-none',
          defaultClassNames.nav,
        ),
        outside: cn(
          'text-muted-foreground aria-selected:text-muted-foreground',
          defaultClassNames.outside,
        ),
        range_end: cn('bg-accent rounded-r-md', defaultClassNames.range_end),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_start: cn('bg-accent rounded-l-md', defaultClassNames.range_start),
        root: cn('w-fit', defaultClassNames.root),
        table: 'w-full table-fixed border-collapse',
        today: cn('rounded-md', defaultClassNames.today),
        week: cn('mt-1.5 flex w-full', defaultClassNames.week),
        week_number: cn(
          'text-muted-foreground select-none text-[0.8rem]',
          defaultClassNames.week_number,
        ),
        week_number_header: cn('w-[--cell-size] select-none', defaultClassNames.week_number_header),
        weekday: cn(
          'text-muted-foreground flex-1 select-none rounded-md text-[0.8rem] font-normal',
          defaultClassNames.weekday,
        ),
        weekdays: cn('flex', defaultClassNames.weekdays),
        ...classNames,
      }}
      components={{
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('size-5', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('size-5', className)} {...props} />;
          }

          return <ChevronDownIcon className={cn('size-5', className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        Dropdown: (props) => {
          const { onChange, options, value } = props;
          const selectedValue = value?.toString() ?? '';
          const selectOptions =
            options?.map((opt) => ({
              label: opt.label,
              value: opt.value.toString(),
            })) ?? [];

          const selectedOption = selectOptions.find((opt) => opt.value === selectedValue);

          const handleChange = (newValue: string) => {
            const changeEvent = {
              target: { value: newValue },
            } as React.ChangeEvent<HTMLSelectElement>;
            onChange?.(changeEvent);
          };

          return (
            <div className="-mt-[10px]">
              <Select onValueChange={handleChange} value={selectedValue}>
                <SelectTrigger className="hover:bg-accent hover:text-accent-foreground border-border bg-background pointer-events-auto h-8 min-w-[68px] gap-1 rounded-md border py-1 pr-1 pl-2 text-xs shadow-[0px_1px_2px_0px_rgba(0,0,0,0.1)] focus:ring-0">
                  <SelectValue placeholder={selectedValue}>{selectedOption?.label}</SelectValue>
                </SelectTrigger>
                <SelectContent align="start" className="max-h-80" position="popper">
                  {selectOptions.map((option, id) => (
                    <SelectItem
                      className="text-xs"
                      key={`${option.value}-${id}`}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        },
        Root: ({ className, rootRef, ...props }) => {
          return <div className={cn(className)} data-slot="calendar" ref={rootRef} {...props} />;
        },
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="flex size-[--cell-size] items-center justify-center text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      endMonth={props.endMonth ?? new Date(new Date().getFullYear() + 10, 11)}
      fixedWeeks={fixedWeeks}
      formatters={{
        formatMonthDropdown: (date) => date.toLocaleString(i18n.language, { month: 'short' }),
        ...formatters,
      }}
      showOutsideDays={showOutsideDays}
      startMonth={props.startMonth ?? new Date(new Date().getFullYear() - 100, 0)}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  const isFocused = modifiers['focused'];

  React.useEffect(() => {
    if (isFocused) {
      ref.current?.focus();
    }
  }, [isFocused]);

  return (
    <Button
      className={cn(
        'group-data-[focused=true]/day:border-ring group-data-[focused=true]/day:ring-ring/50 flex h-8 w-8 items-center justify-center rounded-md bg-transparent text-sm leading-none font-normal text-foreground group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-[3px] hover:bg-transparent data-[range-end=true]:bg-primary data-[range-end=true]:text-primary-foreground data-[range-start=true]:bg-primary data-[range-start=true]:text-primary-foreground data-[selected-single=true]:bg-primary data-[selected-single=true]:text-primary-foreground',
        defaultClassNames.day,
        className,
      )}
      data-day={day.date.toLocaleDateString()}
      data-range-end={modifiers['range_end']}
      data-range-middle={modifiers['range_middle']}
      data-range-start={modifiers['range_start']}
      data-selected-single={
        modifiers['selected'] &&
        !modifiers['range_start'] &&
        !modifiers['range_end'] &&
        !modifiers['range_middle']
      }
      variant="ghost"
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
